import {Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';
import BusinessFeesFilters from '@/screens/Merchants/Fee/BusinessFeesFilters';
import BusinessFeesTable from '@/screens/Merchants/Fee/BusinessFeesTable';
import useFeeList from '@/screens/Merchants/Fee/useFeeList';

import BusinessScopeNotice from '../BusinessScopeNotice';

type BusinessFeesTabProps = {
  businessId: string;
  businessName?: string;
  /** Test hook prefix; each rendered element namespaces under it. */
  dataCy?: string;
};

const BusinessFeesTab = ({
  businessId,
  businessName,
  dataCy,
}: BusinessFeesTabProps) => {
  const {
    isLoading,
    fetchList,
    refetchCurrentPage,
    displayPage,
    pageInfo,
    list,
    isPageReset,
    isSearch,
    selectCurrentFilters,
    currentFilters,
    handleAutoComplete,
    clearFilters,
  } = useFeeList({businessId, enabled: Boolean(businessId)});

  const label = businessName || 'this enterprise';

  const renderList = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!list || !list.length) {
      return (
        <p
          data-cy={dataCy ? `${dataCy}-empty` : undefined}
          className="py-10 text-center text-gray-500 dark:text-gray-400"
        >
          No fees found for this enterprise.
        </p>
      );
    }

    return (
      <BusinessFeesTable
        items={list}
        onFeeSaved={refetchCurrentPage}
        businessId={businessId}
        businessName={businessName}
        dataCy={dataCy}
      />
    );
  };

  return (
    <section className="px-3 pt-3">
      <div data-cy={dataCy ? `${dataCy}-scope-notice` : undefined}>
        <BusinessScopeNotice
          lead="Showing fees specific to"
          name={label}
          tail=". Organization default fees are managed under Merchants > Fees."
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h3
          data-cy={dataCy ? `${dataCy}-title` : undefined}
          className="font-semibold text-gray-900 dark:text-gray-100"
        >
          Enterprise Fees — {label}
        </h3>
        <BusinessFeesFilters
          dataCy={dataCy ? `${dataCy}-create` : undefined}
          formDataCy={dataCy ? `${dataCy}-form` : undefined}
          businessId={businessId}
          businessName={businessName}
          handleAutoComplete={handleAutoComplete}
          currentFilters={currentFilters}
          selectCurrentFilters={selectCurrentFilters}
          clearFilters={clearFilters}
          onFeeSaved={refetchCurrentPage}
        />
      </div>

      <div className="pt-5 md:pt-5">{renderList()}</div>

      {list && list.length ? (
        <Pagination
          onPageChange={fetchList as PaginationProps['onPageChange']}
          total={pageInfo.total}
          isSearch={isSearch}
          isPageReset={isPageReset}
          currentPageOverride={displayPage}
        />
      ) : null}
    </section>
  );
};

export default BusinessFeesTab;
